import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import moment from 'moment';
import {ToastrService} from 'ngx-toastr';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import TestCategoryModel from 'src/app/services/testCategory.model';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';
import { TestCategoryService } from 'src/app/services/test-category.service';

@Component({
  selector: 'app-test-categories',
  templateUrl: './test-categories.component.html',
  styleUrls: ['./test-categories.component.scss']
})
export class TestCategoriesComponent implements OnInit, OnDestroy {
  public testCategories: TestCategoryModel[] = [];
  public testCategoriesList: TestCategoryModel[] = [];
  public testCategory: TestCategoryModel = new TestCategoryModel();
  public filteredTestCategories: TestCategoryModel[] = [];
  public search: string;
  registerForm: FormGroup;
  submitted = false;
  public submitButton: boolean;
  public updateButton: boolean;
  public testCategoryDoubleClick: boolean;
  public NoDataMessage: boolean = false;
  public activePageNo: number;
  public clearSearchInput: boolean = false;
  public enableDisablePageLoader: boolean = false;
  public listLoader = false;              // Show listLoader spinner
  public startIndex = 0;             // Tracks how much has been loaded
  public isFiltering: boolean = false;
  public noList: boolean = false;
  searchTimeout: any;
  public formFieldCount: number = 0;
  public showForm: boolean = false;

  constructor(private toastrService: ToastrService, public formBuilder: FormBuilder, public testCategoryService: TestCategoryService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.testCategory = new TestCategoryModel();
    /*form validation*/
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      abbreviation: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  private batchLoader: BatchLoader<any>;

  public async ngOnInit() {
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.testCategories = [];
    this.testCategoriesList = [];
    this.filteredTestCategories = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.testCategories = await this.testCategoryService.getAllTestCategory();
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.enableDisablePageLoader = false;
    }
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private scrollTimeout: any;

  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 50 && !this.batchLoader.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 100);
  }

  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredTestCategories : this.testCategories,
      (updatedList) => {
        this.testCategoriesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  //To cancel ongoing requests and unsubscribe from subscriptions.
  public ngOnDestroy(): void {
    this.testCategoryService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /*// test category register form convenience getter for easy access to form fields*/
  get testCategoryFormValidation() {
    return this.registerForm.controls;
  }

  // search functionality
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.testCategories.filter(route =>
        route.name?.toLowerCase().includes(query)
      )
      : this.testCategories;
    this.filteredTestCategories = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.testCategoriesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.testCategory = new TestCategoryModel();
    this.submitButton = false;
    this.updateButton = false;
    this.testCategoryDoubleClick = false;
    this.submitted = false;
  }

  /*Add Test Category*/
  public async addUpdateTestCategory() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    try {
      this.testCategoryDoubleClick = true;
      // GET FORM VALUES
      const formData = this.registerForm.value; // contains updated values
      formData.created_name = localStorage.getItem('LoginUserName');
      formData.updated_name = localStorage.getItem('LoginUserName');
      if (this.submitButton) {
        await this.testCategoryService.create(formData);
        this.toastrService.success('Test category created successfully', '', {
          timeOut: 3000
        });
      } else {
        const updateTestCategory = {...formData}
        delete updateTestCategory.id;
        await this.testCategoryService.updateOneByID(this.testCategory.id, updateTestCategory);
        this.toastrService.success('Test category updated successfully', '', {
          timeOut: 3000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.testCategoryDoubleClick = false;
    }
  }

  /*Get test category by id*/
  public getTestCategoryById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.updateButton = true;
    this.testCategoryDoubleClick = false;
    const foundTestCategory = this.testCategoriesList.find(uom => uom?.id === id);
    this.testCategory = foundTestCategory ? {...foundTestCategory} : new TestCategoryModel(); // clone object
    if (this.testCategory) {
      this.registerForm.patchValue({
        name: this.testCategory.name,
        abbreviation: this.testCategory.abbreviation,
        description: this.testCategory.description
      });
    }
  }

  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
